import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { useUIStore } from '../../stores/useUIStore';
import { webLog } from '../grpc/webLogger';
import { getPipeexUiProfileMetadata } from './pipeexProfileUiState';
import { buildPcfSettingsDtoFromStore } from './pcfSettingsDtoBuilder';
import { SettingsType, type PcfSettingsDebugSummary, type PcfSettingsDto, type PcfSettingsStoreDebugSnapshot } from './pcfSettingsTypes';
import { getTextValue, normalizePcfToken, parseJson, unwrapLegacyValue } from './pcfSettingsSerialization';

type PcfSettingsDebugLogSource = 'pcfSettingsClient' | 'pcfSettingsApply';

type SerializablePcfSettingsTraceValue =
  | null
  | string
  | number
  | boolean
  | SerializablePcfSettingsTraceValue[]
  | { [key: string]: SerializablePcfSettingsTraceValue };

interface PcfSettingsTraceEntry {
  sequence: number;
  timestamp: string;
  source: PcfSettingsDebugLogSource;
  message: string;
  payload: SerializablePcfSettingsTraceValue;
}

type PcfSettingsDebugWindow = Window & {
  __PIPEEX_MAPPING_TRACE__?: boolean;
  __PIPEEX_PCF_SETTINGS_TRACE__?: PcfSettingsTraceEntry[];
  dumpPcfSettingsTrace?: () => PcfSettingsTraceEntry[];
  copyPcfSettingsTrace?: () => string;
};

const MaxPcfSettingsTraceEntries = 500;
let pcfSettingsTraceSequence = 0;

function isEnabledFlag(value: string | null | undefined): boolean {
  return value === '1' || value === 'true';
}

function getPcfSettingsDebugWindow(): PcfSettingsDebugWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as PcfSettingsDebugWindow;
}

function isPcfSettingsDebugLoggingEnabled(): boolean {
  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
    return false;
  }

  if (getPcfSettingsDebugWindow()?.__PIPEEX_MAPPING_TRACE__ === true) {
    return true;
  }

  try {
    return (
      isEnabledFlag(window.localStorage?.getItem('pipeex:mappingTrace')) ||
      isEnabledFlag(window.localStorage?.getItem('pipeex:webLogVerbose'))
    );
  } catch {
    return false;
  }
}

function stringifyDebugPayload(payload: unknown): string {
  try {
    return JSON.stringify(normalizePcfSettingsTracePayload(payload));
  } catch {
    return '"[Unserializable payload]"';
  }
}

function serializePcfSettingsTraceValue(
  value: unknown,
  seen: WeakSet<object>
): SerializablePcfSettingsTraceValue {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ?? null,
    };
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => serializePcfSettingsTraceValue(item, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);
    const result: { [key: string]: SerializablePcfSettingsTraceValue } = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      result[key] = serializePcfSettingsTraceValue(entryValue, seen);
    });
    seen.delete(value);
    return result;
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  return String(value);
}

function normalizePcfSettingsTracePayload(payload?: unknown): SerializablePcfSettingsTraceValue {
  return serializePcfSettingsTraceValue(payload ?? {}, new WeakSet<object>());
}

function rememberPcfSettingsTrace(entry: PcfSettingsTraceEntry): void {
  const debugWindow = getPcfSettingsDebugWindow();
  if (!debugWindow) {
    return;
  }

  const buffer = debugWindow.__PIPEEX_PCF_SETTINGS_TRACE__ ?? [];
  buffer.push(entry);
  if (buffer.length > MaxPcfSettingsTraceEntries) {
    buffer.splice(0, buffer.length - MaxPcfSettingsTraceEntries);
  }

  debugWindow.__PIPEEX_PCF_SETTINGS_TRACE__ = buffer;
  debugWindow.dumpPcfSettingsTrace = () => [...(debugWindow.__PIPEEX_PCF_SETTINGS_TRACE__ ?? [])];
  debugWindow.copyPcfSettingsTrace = () => {
    const text = JSON.stringify(debugWindow.__PIPEEX_PCF_SETTINGS_TRACE__ ?? [], null, 2);
    void debugWindow.navigator?.clipboard?.writeText(text);
    return text;
  };
}

export function logPcfSettingsDebug(
  source: PcfSettingsDebugLogSource,
  message: string,
  payload?: unknown
): void {
  if (!isPcfSettingsDebugLoggingEnabled()) {
    return;
  }

  const normalizedPayload = normalizePcfSettingsTracePayload(payload);
  const entry: PcfSettingsTraceEntry = {
    sequence: pcfSettingsTraceSequence + 1,
    timestamp: new Date().toISOString(),
    source,
    message,
    payload: normalizedPayload,
  };

  pcfSettingsTraceSequence = entry.sequence;
  rememberPcfSettingsTrace(entry);

  const formattedPayload = stringifyDebugPayload(normalizedPayload);
  webLog.info(source, `[trace#${entry.sequence}] ${message} ${formattedPayload}`);
  console.info(
    `[PcfSettingsTrace][trace#${entry.sequence}] ${source} ${message} ${formattedPayload}`,
    normalizedPayload
  );
}

export function summarizePcfSettingsDtoForDebug(dto: PcfSettingsDto): PcfSettingsDebugSummary {
  const advancedRaw = parseJson<any>(dto.advancedSettings, {});
  const profileUiMetadata = getPipeexUiProfileMetadata(dto.advancedSettings);
  const bom = parseJson<any>(dto.bomParameters, {});
  const isoRaw = parseJson<any>(dto.isoParameters, {});
  const validationMap = parseJson<Array<{ key?: string; Key?: string; value?: string; Value?: string }>>(
    dto.validationMap,
    []
  );

  const projectParameters = Array.isArray(bom?.ProjectParameters) ? bom.ProjectParameters : [];
  const systemParameters = Array.isArray(bom?.SystemParameters) ? bom.SystemParameters : [];
  const bomParameters = Array.isArray(bom?.BomParameters) ? bom.BomParameters : [];
  const isoEntries = Array.isArray(isoRaw) ? isoRaw : [];
  const isoObjectTypes = new Set(
    isoEntries
      .map((entry: any) => getTextValue(entry?.objectType ?? entry?.ObjectType))
      .filter((value) => value.trim().length > 0)
  );
  const validationEntries = validationMap.map((entry) => ({
    key: normalizePcfToken((entry.key || entry.Key || '').trim()),
    value: (entry.value || entry.Value || '').trim(),
  }));
  const quickFillProperties = [
    ...(profileUiMetadata?.quickFill?.properties || []),
    ...(profileUiMetadata?.quickFill?.extraProperties || []),
  ];
  const quickFillHas = (id: string) =>
    quickFillProperties.some(
      (property) =>
        property.id === id &&
        property.checked &&
        typeof property.revitParameter === 'string' &&
        property.revitParameter.trim().length > 0
    );
  const formatTarget = (target?: { parameterDestination: string; parameterBindingKind: string }) =>
    target ? `${target.parameterDestination}/${target.parameterBindingKind}` : null;

  return {
    settingsType: dto.settingsType,
    workflowOption: profileUiMetadata?.workflowOption || null,
    selectedObjectType: '',
    hasSkeyMapping: validationEntries.some((entry) => entry.key === 'SKEY' && entry.value),
    hasObjectTypeMapping: validationEntries.some((entry) => entry.key === 'OBJECTTYPE' && entry.value),
    connectorBindings: {
      endpoint1: quickFillHas('end1'),
      endpoint2: quickFillHas('end2'),
      endpoint3: quickFillHas('end3'),
      endpoint4: quickFillHas('end4'),
      material: quickFillHas('material'),
      designStd: quickFillHas('design-std'),
      pressureClass: quickFillHas('pressure-class'),
    },
    processFlags: {
      processConditionsChecked:
        profileUiMetadata?.workflow.processConditionsChecked ||
        Boolean(advancedRaw?.pipeexUi && parseJson<any>(advancedRaw.pipeexUi, {})?.workflow?.processConditionsChecked) ||
        false,
      projectPropertiesChecked:
        profileUiMetadata?.workflow.projectPropertiesChecked ||
        Boolean(advancedRaw?.pipeexUi && parseJson<any>(advancedRaw.pipeexUi, {})?.workflow?.projectPropertiesChecked) ||
        false,
    },
    parameterTargets: {
      connector: formatTarget(profileUiMetadata?.wizard?.connector),
      bolt: formatTarget(profileUiMetadata?.wizard?.bolt),
      additionalCategories: formatTarget(profileUiMetadata?.wizard?.additionalCategories),
      mappingRepair: formatTarget(profileUiMetadata?.wizard?.connector),
    },
    counts: {
      projectParameters: projectParameters.length,
      systemParameters: systemParameters.length,
      bomParameters: bomParameters.length,
      isoObjectTypes: isoObjectTypes.size,
      isoAttributes: isoEntries.length,
      validationEntries: validationEntries.length,
    },
    validationKeys: validationEntries.map((entry) => entry.key),
  };
}

const MaxDetailedParameterEntries = 160;

function getLegacyTextValue(value: unknown): string {
  return getTextValue(unwrapLegacyValue(value));
}

function summarizeParameterEntries(entries: any[]): Array<{
  index: number;
  name: string;
  revitParameter: string;
  parameterType: string;
  order: string;
}> {
  return entries.slice(0, MaxDetailedParameterEntries).map((entry, index) => ({
    index,
    name: getLegacyTextValue(entry?.Name ?? entry?.name),
    revitParameter: getLegacyTextValue(entry?.RevitParameter ?? entry?.revitParameter),
    parameterType: getLegacyTextValue(entry?.ParameterType ?? entry?.parameterType),
    order: getLegacyTextValue(entry?.POrder ?? entry?.pOrder ?? entry?.Order ?? entry?.order),
  }));
}

function summarizeIsoEntries(entries: any[]): Array<{
  index: number;
  objectType: string;
  pcfAttribute: string;
  revitParameter: string;
  isoType: string;
  order: string;
  hasScript: boolean;
}> {
  return entries.slice(0, MaxDetailedParameterEntries).map((entry, index) => {
    const script = getLegacyTextValue(entry?.Script ?? entry?.script);

    return {
      index,
      objectType: getLegacyTextValue(entry?.ObjectType ?? entry?.objectType),
      pcfAttribute: getLegacyTextValue(entry?.PcfName ?? entry?.pcfName ?? entry?.Name ?? entry?.name),
      revitParameter: getLegacyTextValue(entry?.ParName ?? entry?.parName ?? entry?.RevitParameter ?? entry?.revitParameter),
      isoType: getLegacyTextValue(entry?.IsoType ?? entry?.isoType),
      order: getLegacyTextValue(entry?.CSort ?? entry?.cSort ?? entry?.POrder ?? entry?.pOrder),
      hasScript: script.trim().length > 0,
    };
  });
}

function countByObjectType(entries: any[]): Record<string, number> {
  return entries.reduce<Record<string, number>>((counts, entry) => {
    const objectType = getLegacyTextValue(entry?.ObjectType ?? entry?.objectType) || '(blank)';
    counts[objectType] = (counts[objectType] ?? 0) + 1;
    return counts;
  }, {});
}

function summarizeScopeEditorState(scopeEditor: {
  deletedRowIds?: string[];
  customRows?: Array<{ id?: string; label?: string; value?: string }>;
  pcfAttributeNames?: Record<string, string>;
} | null | undefined): {
  deletedRowIds: string[];
  customRows: Array<{ id?: string; label?: string; value?: string }>;
  renamedPcfAttributes: Record<string, string>;
} {
  return {
    deletedRowIds: scopeEditor?.deletedRowIds ?? [],
    customRows: scopeEditor?.customRows ?? [],
    renamedPcfAttributes: scopeEditor?.pcfAttributeNames ?? {},
  };
}

export function summarizePcfSettingsDtoDataFlowForDebug(dto: PcfSettingsDto): Record<string, unknown> {
  const advancedRaw = parseJson<any>(dto.advancedSettings, {});
  const profileUiMetadata = getPipeexUiProfileMetadata(dto.advancedSettings);
  const wizard = profileUiMetadata?.wizard;
  const bom = parseJson<any>(dto.bomParameters, {});
  const isoRaw = parseJson<any>(dto.isoParameters, []);
  const validationMap = parseJson<Array<{ key?: string; Key?: string; value?: string; Value?: string }>>(
    dto.validationMap,
    []
  );
  const projectParameters = Array.isArray(bom?.ProjectParameters) ? bom.ProjectParameters : [];
  const systemParameters = Array.isArray(bom?.SystemParameters) ? bom.SystemParameters : [];
  const bomParameters = Array.isArray(bom?.BomParameters) ? bom.BomParameters : [];
  const isoEntries = Array.isArray(isoRaw) ? isoRaw : [];
  const checkedQuickFillProperties = [
    ...(profileUiMetadata?.quickFill?.properties ?? []),
    ...(profileUiMetadata?.quickFill?.extraProperties ?? []),
  ]
    .filter((property) => property.checked || property.revitParameter)
    .map((property) => ({
      id: property.id,
      label: property.label,
      checked: property.checked,
      revitParameter: property.revitParameter,
      isInstance: property.isInstance ?? null,
    }));
  const validationEntries = validationMap.map((entry) => ({
    key: normalizePcfToken((entry.key || entry.Key || '').trim()),
    value: (entry.value || entry.Value || '').trim(),
  }));

  return {
    summary: summarizePcfSettingsDtoForDebug(dto),
    payloadSizes: {
      advancedSettings: dto.advancedSettings.length,
      allTags: dto.allTags.length,
      bomParameters: dto.bomParameters.length,
      isoParameters: dto.isoParameters.length,
      validationMap: dto.validationMap.length,
    },
    advancedSettings: {
      hasBase: Boolean(advancedRaw?.base),
      pipeexUiVersion: profileUiMetadata?.version ?? null,
      hasWizard: Boolean(wizard),
      workflowOption: profileUiMetadata?.workflowOption ?? null,
      workflow: profileUiMetadata?.workflow ?? null,
      wizard: wizard
        ? {
            setup: {
              workflowMode: wizard.setup.workflowMode,
              useDefaultFile: wizard.setup.useDefaultFile,
              sharedParameterFilePath: wizard.setup.sharedParameterFilePath,
              selectedCategories: wizard.setup.selectedCategories,
              systemGroupingPreference: wizard.setup.systemGroupingPreference,
              parameters: wizard.setup.parameters,
            },
            menu: wizard.menu,
            connector: wizard.connector,
            bolt: wizard.bolt,
            additionalCategories: wizard.additionalCategories,
            processProperties: wizard.processProperties,
            mapping: wizard.mapping,
            progress: wizard.progress,
          }
        : null,
      legacyProcessProperties: profileUiMetadata?.processProperties ?? null,
      quickFill: {
        checkedOrMappedProperties: checkedQuickFillProperties,
      },
      scopeEditors: {
        processConditions: summarizeScopeEditorState(profileUiMetadata?.scopeEditors?.processConditions),
        projectProperties: summarizeScopeEditorState(profileUiMetadata?.scopeEditors?.projectProperties),
      },
    },
    bomParameters: {
      projectParameters: summarizeParameterEntries(projectParameters),
      systemParameters: summarizeParameterEntries(systemParameters),
      bomColumns: summarizeParameterEntries(bomParameters),
      omittedCounts: {
        projectParameters: Math.max(0, projectParameters.length - MaxDetailedParameterEntries),
        systemParameters: Math.max(0, systemParameters.length - MaxDetailedParameterEntries),
        bomColumns: Math.max(0, bomParameters.length - MaxDetailedParameterEntries),
      },
    },
    isoParameters: {
      total: isoEntries.length,
      byObjectType: countByObjectType(isoEntries),
      entries: summarizeIsoEntries(isoEntries),
      omittedCount: Math.max(0, isoEntries.length - MaxDetailedParameterEntries),
    },
    validationMap: validationEntries,
  };
}

export function summarizeCurrentPcfSettingsForDebug(
  settingsType: SettingsType = SettingsType.User
): PcfSettingsDebugSummary {
  return summarizePcfSettingsDtoForDebug(buildPcfSettingsDtoFromStore(settingsType));
}

export function snapshotCurrentPcfSettingsStoreForDebug(): PcfSettingsStoreDebugSnapshot {
  const state = useConfigurationStore.getState();
  const selectedWorkflowOption = useUIStore.getState().selectedOption;
  const allObjectType =
    state.mappingConfiguration.general.mappingTable.objectType.find(
      (objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL'
    ) || null;
  const allAttributes = allObjectType?.attributes || [];
  const mappedAllAttributes = allAttributes.filter(
    (attribute) => typeof attribute.revitParam === 'string' && attribute.revitParam.trim().length > 0
  );

  return {
    selectedWorkflowOption,
    menu: {
      objectTypeValue: state.menuConfiguration.objectTypeValue,
      sKeyValue: state.menuConfiguration.sKeyValue,
      pipingSpecValue: state.menuConfiguration.pipingSpecValue,
      systemGroupingValue: state.menuConfiguration.systemGroupingValue,
      groupingParameterName: state.menuConfiguration.groupingParameterName,
      groupingParameterSource: state.menuConfiguration.groupingParameterSource,
    },
    connector: {
      endpoint1Value: state.connectorBindingConfiguration.endpoint1Value.value,
      endpoint2Value: state.connectorBindingConfiguration.endpoint2Value.value,
      endpoint3Value: state.connectorBindingConfiguration.endpoint3Value.value,
      endpoint4Value: state.connectorBindingConfiguration.endpoint4Value.value,
      materialValue: state.connectorBindingConfiguration.materialValue.value,
      designStdValue: state.connectorBindingConfiguration.designStdValue.value,
      pressureClassValue: state.connectorBindingConfiguration.pressureClassValue.value,
      insulationSpecValue: state.connectorBindingConfiguration.insulationSpecValue.value,
      insulationThicknessValue: state.connectorBindingConfiguration.insulationThicknessValue.value,
      weightValue: state.connectorBindingConfiguration.weightValue.value,
      tracingSpecValue: state.connectorBindingConfiguration.tracingSpecValue.value,
      itemDescriptionValue: state.connectorBindingConfiguration.itemDescriptionValue.value,
    },
    process: {
      processConditionsChecked: state.processPropertiesConfiguration.processConditionsChecked,
      projectPropertiesChecked: state.processPropertiesConfiguration.projectPropertiesChecked,
      pipingSpecValuePC: state.processPropertiesConfiguration.pipingSpecValuePC.value,
      tracingSpecValuePC: state.processPropertiesConfiguration.tracingSpecValuePC.value,
      projectIdentifierValue: state.processPropertiesConfiguration.projectIdentifierValue.value,
      areaValue: state.processPropertiesConfiguration.areaValue.value,
    },
    bolt: {
      boltSetActiveValue: state.boltConfiguration.boltSetActiveValue.value,
      boltBaseFamilyValue: state.boltConfiguration.boltBaseFamilyValue.value,
      boltModelingChoice: state.boltConfiguration.boltModelingChoice.value,
      boltDiaValue: state.boltConfiguration.boltDiaValue.value,
      boltQuantityValue: state.boltConfiguration.boltQuantityValue.value,
      boltLengthValue: state.boltConfiguration.boltLengthValue.value,
      boltItemDescriptionValue: state.boltConfiguration.boltItemDescriptionValue.value,
      boltMaterialValue: state.boltConfiguration.boltMaterialValue.value,
      boltStandardValue: state.boltConfiguration.boltStandardValue.value,
    },
    mapping: {
      allObjectTypeAttributeCount: allAttributes.length,
      allObjectTypeMappedAttributeCount: mappedAllAttributes.length,
      projectPropertiesCount: state.mappingConfiguration.general.projectProperties.length,
      pipingSystemPropertiesCount: state.mappingConfiguration.general.pipingSystemProperties.length,
    },
  };
}
